/**
 * Filter Types
 */

import type { ContractState, UrgencyLevel } from './contract';

// Filter and search types
/**
 * The Assessments list filters a Dashboard Widget table row links with, in the order the "Filtered from dashboard"
 * chip lists them. The one list the URL reading, the filter type and the chip are all derived from.
 * - completed_*: Completed on or after / before this date (YYYY-MM-DD)
 * - include_not_completed: with completed_*, also the Assessments that have no completed date yet; only `true`
 * - remediated_*, expired_*: a date range in the same format
 * - division_id: comma-separated Division ids
 * - gap_priority: the Assessment has an open Gap of this priority
 * - gap_domain_id: the Assessment has an open Gap in this Assessment Domain
 */
export const DRILL_DOWN_FILTER_KEYS = [
  'completed_from',
  'completed_to',
  'include_not_completed',
  'remediated_from',
  'remediated_to',
  'expired_from',
  'expired_to',
  'division_id',
  'tier_id',
  'gap_priority',
  'gap_domain_id'
] as const;

export type DrillDownFilterKey = (typeof DRILL_DOWN_FILTER_KEYS)[number];

/** The drill-down filters of the Assessments list; each is optional and carried as text. */
export type DrillDownFilters = Partial<Record<DrillDownFilterKey, string>>;

export interface AssessmentFilters extends DrillDownFilters {
  status?: string;
  searchTerm?: string;
  columnFilters?: string; // JSON-encoded per-column filters
  assessor_id?: number;
  supplier_id?: number;
  contract_id?: number;
  /** Assessment Meeting Date on or before this date (YYYY-MM-DD) */
  meeting_date_to?: string;
  evidence_due_from?: string;
  evidence_due_to?: string;
  min_escalation_level?: number;
  /** `contract`: keep only the Contract Owner / Manager part of the caller's scope */
  scope?: 'contract';
  sortBy?: string;
  order?: 'ASC' | 'DESC';
  limit?: number;
  offset?: number;
}

export interface ContractFilters {
  // Basic filters
  status?: string;
  searchTerm?: string;
  searchColumns?: string[]; // Columns to search in (based on user's visible columns)
  columnFilters?: string; // JSON-encoded per-column filters
  sortBy?: string;
  order?: 'ASC' | 'DESC';
  limit?: number;
  offset?: number;

  // State-based filters (matches backend)
  state?: ContractState; // pending_data_enrichment, ready_for_scheduling, scheduled

  // Role-based filters
  unassigned?: boolean; // Any of manager/owner/assessor are not assigned
  processedByMe?: boolean; // Only items processed by logged in user

  // Calculated field filters
  urgency?: UrgencyLevel; // critical, late, ready

  // Meeting-related filters
  scheduled?: boolean; // Only items with meetings scheduled

  // Reassessment status filter (maps to dedicated query param)
  reassessmentStatus?: 'Reassessment Due' | 'Ready for Scheduling';
}
